/**
 * Utilitários para manipulação de cores e geração de paletas harmônicas
 */

/**
 * Converte hex para RGB
 * @param {string} hex - Cor em formato hex (#RRGGBB)
 * @returns {Object} - {r, g, b}
 */
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/**
 * Converte RGB para hex
 * @param {number} r - Red (0-255)
 * @param {number} g - Green (0-255)
 * @param {number} b - Blue (0-255)
 * @returns {string} - Cor em formato hex
 */
export const rgbToHex = (r, g, b) => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

/**
 * Converte hex para HSL
 * @param {string} hex - Cor em formato hex
 * @returns {Object} - {h, s, l}
 */
export const hexToHsl = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;

  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

/**
 * Converte HSL para hex
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} - Cor em formato hex
 */
export const hslToHex = (h, s, l) => {
  h = h / 360;
  s = s / 100;
  l = l / 100;

  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };

  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return rgbToHex(Math.round(r * 255), Math.round(g * 255), Math.round(b * 255));
};

/**
 * Escurece uma cor
 * @param {string} hex - Cor em formato hex
 * @param {number} amount - Quantidade para escurecer (0-1)
 * @returns {string} - Cor escurecida
 */
export const darken = (hex, amount) => {
  const hsl = hexToHsl(hex);
  if (!hsl) return hex;
  
  hsl.l = Math.max(0, hsl.l - (amount * 100));
  return hslToHex(hsl.h, hsl.s, hsl.l);
};

/**
 * Clareia uma cor
 * @param {string} hex - Cor em formato hex
 * @param {number} amount - Quantidade para clarear (0-1)
 * @returns {string} - Cor clareada
 */
export const lighten = (hex, amount) => {
  const hsl = hexToHsl(hex);
  if (!hsl) return hex;
  
  hsl.l = Math.min(100, hsl.l + (amount * 100));
  return hslToHex(hsl.h, hsl.s, hsl.l);
};

/**
 * Ajusta a saturação de uma cor
 * @param {string} hex - Cor em formato hex
 * @param {number} amount - Quantidade para ajustar (-1 a 1)
 * @returns {string} - Cor com saturação ajustada
 */
export const adjustSaturation = (hex, amount) => {
  const hsl = hexToHsl(hex);
  if (!hsl) return hex;
  
  hsl.s = Math.max(0, Math.min(100, hsl.s + (amount * 100)));
  return hslToHex(hsl.h, hsl.s, hsl.l);
};

/**
 * Gera uma cor com opacidade
 * @param {string} hex - Cor em formato hex
 * @param {number} alpha - Opacidade (0-1)
 * @returns {string} - Cor em formato rgba
 */
export const withOpacity = (hex, alpha) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

/**
 * Calcula o contraste entre duas cores
 * @param {string} color1 - Primeira cor em hex
 * @param {string} color2 - Segunda cor em hex
 * @returns {number} - Ratio de contraste
 */
export const getContrastRatio = (color1, color2) => {
  const getLuminance = (hex) => {
    const rgb = hexToRgb(hex);
    if (!rgb) return 0;
    
    const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);
  
  return (Math.max(lum1, lum2) + 0.05) / (Math.min(lum1, lum2) + 0.05);
};

/**
 * Verifica se uma cor é clara ou escura
 * @param {string} hex - Cor em formato hex
 * @returns {boolean} - true se for clara, false se for escura
 */
export const isLight = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return true;
  
  // Usa a fórmula de luminância percebida
  const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return brightness > 128;
};

/**
 * Gera uma paleta harmônica baseada em uma cor principal
 * @param {string} primaryHex - Cor principal em hex
 * @returns {Object} - Paleta de cores harmônica
 */
export const generateHarmoniousPalette = (primaryHex) => {
  const hsl = hexToHsl(primaryHex);
  if (!hsl) return null;

  const isLightColor = isLight(primaryHex);
  
  return {
    primary: primaryHex,
    
    // Cor secundária (complementar suavizada)
    secondary: hslToHex((hsl.h + 180) % 360, Math.max(30, hsl.s * 0.7), hsl.l),
    
    // Tons da cor principal
    primaryLight: lighten(primaryHex, 0.3),
    primaryDark: darken(primaryHex, 0.2),
    primaryVeryLight: lighten(primaryHex, 0.6),
    primaryVeryDark: darken(primaryHex, 0.4),
    
    // Cores com opacidade para fundos
    primaryOpacity10: withOpacity(primaryHex, 0.1),
    primaryOpacity20: withOpacity(primaryHex, 0.2),
    primaryOpacity30: withOpacity(primaryHex, 0.3),
    
    // Cores de texto que contrastam bem
    textOnPrimary: isLightColor ? '#000000' : '#FFFFFF',
    textOnPrimaryLight: isLight(lighten(primaryHex, 0.3)) ? '#000000' : '#FFFFFF',
    
    // Cores de hover
    hoverPrimary: isLightColor ? darken(primaryHex, 0.1) : lighten(primaryHex, 0.1),
    hoverSecondary: isLight(hslToHex((hsl.h + 180) % 360, Math.max(30, hsl.s * 0.7), hsl.l)) 
      ? darken(hslToHex((hsl.h + 180) % 360, Math.max(30, hsl.s * 0.7), hsl.l), 0.1)
      : lighten(hslToHex((hsl.h + 180) % 360, Math.max(30, hsl.s * 0.7), hsl.l), 0.1),
    
    // Cores neutras que harmonizam
    neutral: hslToHex(hsl.h, Math.min(20, hsl.s * 0.3), isLightColor ? 85 : 20),
    neutralLight: hslToHex(hsl.h, Math.min(15, hsl.s * 0.2), isLightColor ? 95 : 15),
    neutralDark: hslToHex(hsl.h, Math.min(25, hsl.s * 0.4), isLightColor ? 60 : 40),
  };
}; 

/**
 * Gera uma versão mais fraca/clara de uma cor para backgrounds
 * @param {string} color - Cor base em formato hex
 * @param {number} intensity - Intensidade (0.1 = muito fraco, 0.9 = forte) - padrão 0.1
 * @returns {string} - Cor resultante em formato hex ou rgba
 */
export const weakenColor = (color, intensity = 0.1) => {
  if (!color) return '#f9f9f9';
  
  // Se a cor não começar com #, adicionar
  const normalizedColor = color.startsWith('#') ? color : `#${color}`;
  
  const rgb = hexToRgb(normalizedColor);
  if (!rgb) return '#f9f9f9';
  
  // Usar rgba para ter transparência, criando efeito de cor fraca
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${intensity})`;
};

/**
 * Gera uma versão pastel de uma cor (mistura com branco)
 * @param {string} color - Cor base em formato hex
 * @param {number} mixRatio - Quanto de branco misturar (0.1 = pouco branco, 0.9 = muito branco) - padrão 0.8
 * @returns {string} - Cor pastel em formato hex
 */
export const pastelColor = (color, mixRatio = 0.8) => {
  if (!color) return '#f9f9f9';
  
  const normalizedColor = color.startsWith('#') ? color : `#${color}`;
  const rgb = hexToRgb(normalizedColor);
  if (!rgb) return '#f9f9f9';
  
  // Misturar com branco (255, 255, 255)
  const r = Math.round(rgb.r + (255 - rgb.r) * mixRatio);
  const g = Math.round(rgb.g + (255 - rgb.g) * mixRatio);
  const b = Math.round(rgb.b + (255 - rgb.b) * mixRatio);
  
  return rgbToHex(r, g, b);
};